/* eslint-disable @typescript-eslint/no-dynamic-delete */
import type { LocationQuery } from 'vue-router';
import type { Filter, SelectedFilter } from '@/types/filter';
// utils
import { sortArrayOfObjectsByKeyAlphabetically } from '@/utils/general';

// components
import CiFilterResetModal from '@/components/filter/CiFilterPanel/CiFilterResetModal.vue';

const rangeToTop = (a, b) => {
  if (a.type === 'range' && b.type !== 'range') {
    return -1;
  } else {
    return 0;
  }
};

export default function useFilter() {
  /********************
   * COMPOSITIONS      *
   ********************/
  const route = useRoute();
  const searchStore = useSearchStore();
  const { $gettext } = useGettext();
  const { openModal, closeModal } = useCiModal();

  /********************
   * REFS & VARS       *
   ********************/
  const selectedFilterGroupLabel = ref('');

  const filter: Filter[] = [
    {
      type: 'bool',
      name: 'fanclub',
      label: $gettext('FANCLUB campsites'),
      group: 'fanclub',
    },
    {
      type: 'rating',
      name: 'rating_avg_overall__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Overall rating'),
      group: 'rating',
    },
    {
      type: 'rating',
      name: 'rating_avg_location__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average location'),
      group: 'rating',
    },
    {
      type: 'rating',
      name: 'rating_avg_sanitary__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average sanitary'),
      group: 'rating',
    },
    {
      type: 'rating',
      name: 'rating_avg_cleanliness_sanitary__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average cleanliness sanitary'),
      group: 'rating',
    },
    {
      type: 'rating',
      name: 'rating_avg_cleanliness_overall__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average cleanliness overall'),
      group: 'rating',
    },
    {
      type: 'rating',
      name: 'rating_avg_price_quality_ratio__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average price quality ratio'),
      group: 'rating',
    },
    {
      type: 'rating',
      name: 'rating_avg_infrastructure__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average infrastructure'),
      group: 'rating',
    },
    {
      type: 'rating',
      name: 'rating_avg_catering__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average catering'),
      group: 'rating',
    },
    {
      type: 'rating',
      name: 'rating_avg_calmness__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average calmness'),
      group: 'rating',
    },
    {
      type: 'rating',
      name: 'rating_avg_leisure__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average leisure'),
      group: 'rating',
    },
    {
      type: 'rating',
      name: 'rating_avg_friendliness__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average friendliness'),
      group: 'rating',
    },
    {
      type: 'rating',
      name: 'rating_avg_condition_rentable_accomodations__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average condition rentable accomondation'),
      group: 'rating',
    },

    {
      type: 'bitwise',
      name: 'hiking',
      param: 'ideal_activities',
      value: 256,
      label: $gettext('Hiking'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'cycling',
      param: 'ideal_activities',
      value: 1024,
      label: $gettext('Cycling'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'mountainbiking',
      param: 'ideal_activities',
      value: 2048,
      label: $gettext('Mountainbiking'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'swimming',
      param: 'ideal_activities',
      value: 1,
      label: $gettext('Swimming'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'wellness',
      param: 'ideal_activities',
      value: 2,
      label: $gettext('Wellness'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'kayak',
      param: 'ideal_activities',
      value: 16,
      label: $gettext('Kayak'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'culture',
      param: 'ideal_activities',
      value: 131072,
      label: $gettext('Culture'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'relaxing',
      param: 'ideal_activities',
      value: 524288,
      label: $gettext('Relaxing'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'ideal_for_sailing',
      param: 'ideal_activities',
      value: 4,
      label: $gettext('Sailing'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'surfing',
      param: 'ideal_activities',
      value: 8,
      label: $gettext('Surfing'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'diving',
      param: 'ideal_activities',
      value: 32,
      label: $gettext('Diving'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'motorboat',
      param: 'ideal_activities',
      value: 64,
      label: $gettext('Motorboat'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'fishing_bw',
      param: 'ideal_activities',
      value: 128,
      label: $gettext('Fishing'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'climbing',
      param: 'ideal_activities',
      value: 512,
      label: $gettext('Climbing'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'paragliding',
      param: 'ideal_activities',
      value: 4096,
      label: $gettext('Paragliding'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'horsebackriding',
      param: 'ideal_activities',
      value: 8192,
      label: $gettext('Horsebackriding'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'tennis_bitwise',
      param: 'ideal_activities',
      value: 16384,
      label: $gettext('Tennis'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'golf_bitwise',
      param: 'ideal_activities',
      value: 32768,
      label: $gettext('Golf'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'sightseeing',
      param: 'ideal_activities',
      value: 65536,
      label: $gettext('Sightseeing'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'strolling',
      param: 'ideal_activities',
      value: 262144,
      label: $gettext('Strolling'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'culinary',
      param: 'ideal_activities',
      value: 1048576,
      label: $gettext('Culinary'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'motorcycle',
      param: 'ideal_activities',
      value: 2097152,
      label: $gettext('Motorcycle'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'party',
      param: 'ideal_activities',
      value: 4194304,
      label: $gettext('Party'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'ski',
      param: 'ideal_activities',
      value: 8388608,
      label: $gettext('Ski'),
      group: 'perfect_for',
    },
    {
      type: 'bitwise',
      name: 'cross_country',
      param: 'ideal_activities',
      value: 16777216,
      label: $gettext('CrossCountry'),
      group: 'perfect_for',
    },
    {
      type: 'bool',
      name: 'bus_bar',
      label: $gettext('Electric hook-ups'),
      group: 'at_the_parking',
    },
    {
      type: 'bool',
      name: 'water_connection_at_pitch',
      label: $gettext('Water connection at pitch'),
      group: 'at_the_parking',
    },
    {
      type: 'bool',
      name: 'sewage_connection_at_pitch',
      label: $gettext('Sewage connection at pitch'),
      group: 'at_the_parking',
    },
    {
      type: 'bool',
      name: 'gas_connection_at_pitch',
      label: $gettext('Gas connection at pitch'),
      group: 'at_the_parking',
    },
    {
      type: 'bool',
      name: 'tv_connection_at_pitch',
      label: $gettext('TV connection at pitch'),
      group: 'at_the_parking',
    },
    {
      type: 'bool',
      name: 'gas_bottle_exchange',
      label: $gettext('Gas bottle exchange'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'ver_entsorgungs_wohnmobile',
      label: $gettext('Motorhome service point'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'central_bonfire_area',
      label: $gettext('Central bonfire area'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'campfire_at_pitch_allowed',
      label: $gettext('Campfire at pitch allowed'),
      group: 'at_the_parking',
    },
    {
      type: 'bool',
      name: 'firewood',
      label: $gettext('Firewood'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'charcoal_grill_allowed',
      label: $gettext('Carcoal grill allowed'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'tables_and_benches',
      label: $gettext('Tables and benches'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'wifi',
      label: $gettext('Wifi'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'internet_terminal',
      label: $gettext('Internet terminal'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'lockers',
      label: $gettext('Lockers'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'lounge',
      label: $gettext('Lounge'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'cooking_facilities',
      label: $gettext('Cooking facilities'),
      group: 'fare_catering',
    },
    {
      type: 'bool',
      name: 'baby_change',
      label: $gettext('Baby changing room'),
      group: 'family_children',
    },
    {
      type: 'bool',
      name: 'washingmachines',
      label: $gettext('Washingmachines'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'laundry_driers',
      label: $gettext('Tumble driers'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'drying_room',
      label: $gettext('Drying room'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'dog_showers',
      label: $gettext('Dog showers'),
      group: 'pets',
    },
    {
      type: 'bool',
      name: 'dog_meadow',
      label: $gettext('Dog meadow'),
      group: 'pets',
    },
    {
      type: 'bool',
      name: 'dog_bathing',
      label: $gettext('Dog bathing'),
      group: 'pets',
    },
    {
      type: 'bool',
      name: 'spaceyouthgroup',
      label: $gettext('Separate space for youth groups'),
      group: 'at_the_parking',
    },
    {
      type: 'bool',
      name: 'restaurant',
      label: $gettext('Restaurant'),
      group: 'fare_catering',
    },
    {
      type: 'bool',
      name: 'snack',
      label: $gettext('Snack'),
      group: 'fare_catering',
    },
    {
      type: 'bool',
      name: 'food_supply',
      label: $gettext('Grocery shop'),
      group: 'fare_catering',
    },
    {
      type: 'bool',
      name: 'bread_in_high_season',
      label: $gettext('Bread service in high season'),
      group: 'fare_catering',
    },
    {
      type: 'bool',
      name: 'bread_in_low_season',
      label: $gettext('Bread service in low season'),
      group: 'fare_catering',
    },
    {
      type: 'bool',
      name: 'childrens_playground',
      label: $gettext('Children\'s playground'),
      group: 'family_children',
    },
    {
      type: 'bool',
      name: 'indoor_gaming_possibilities',
      label: $gettext('Indoor gaming possibilities'),
      group: 'family_children',
    },
    {
      type: 'bool',
      name: 'petting_zoo',
      label: $gettext('Petting zoo'),
      group: 'family_children',
    },
    {
      type: 'bool',
      name: 'kids_animation',
      label: $gettext('Kids animation'),
      group: 'family_children',
    },
    {
      type: 'bool',
      name: 'sanitarian_handicapped',
      label: $gettext('Barrier-free sanitary facilities'),
      group: 'accessibility',
    },
    {
      type: 'bool',
      name: 'washing_cabins',
      label: $gettext('Washing cabins'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'rentable_bathrooms',
      label: $gettext('Rentable bathrooms'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'wheelchair_ramps',
      label: $gettext('Wheelchair ramps'),
      group: 'accessibility',
    },
    {
      type: 'bool',
      name: 'handicapped_accessible_water_entrance',
      label: $gettext('Has a handicapped accessible water entrance'),
      group: 'accessibility',
    },
    {
      type: 'bool',
      name: 'most_ways_paved',
      label: $gettext('Most ways are paved'),
      group: 'accessibility',
    },
    {
      type: 'bool',
      name: 'swimming_nature',
      label: $gettext('Swimming in nature'),
      group: 'swimming_wellness',
    },
    {
      type: 'bool',
      name: 'sandbeach',
      label: $gettext('Sand beach'),
      group: 'swimming_wellness',
    },
    {
      type: 'bool',
      name: 'nudist_beach',
      label: $gettext('Nudist beach'),
      group: 'swimming_wellness',
    },
    {
      type: 'bool',
      name: 'swimming_pool_outdoor',
      label: $gettext('Outdoor pool'),
      group: 'swimming_wellness',
    },
    {
      type: 'bool',
      name: 'swimming_pool_indoor',
      label: $gettext('Indoor pool'),
      group: 'swimming_wellness',
    },
    {
      type: 'bool',
      name: 'hot_springs',
      label: $gettext('Hot springs'),
      group: 'swimming_wellness',
    },
    {
      type: 'bool',
      name: 'sauna',
      label: $gettext('Sauna'),
      group: 'swimming_wellness',
    },
    {
      type: 'bool',
      name: 'water_slide',
      label: $gettext('Waterslide'),
      group: 'swimming_wellness',
    },
    {
      type: 'bool',
      name: 'tennis',
      label: $gettext('Tennis'),
      group: 'leisure_opportunities',
    },
    {
      type: 'bool',
      name: 'table_tennis',
      label: $gettext('Table tennis'),
      group: 'leisure_opportunities',
    },
    {
      type: 'bool',
      name: 'volleyball',
      label: $gettext('Volleyball'),
      group: 'leisure_opportunities',
    },
    {
      type: 'bool',
      name: 'mini_golf',
      label: $gettext('Minigolf'),
      group: 'leisure_opportunities',
    },
    {
      type: 'bool',
      name: 'golf',
      label: $gettext('Golf'),
      group: 'leisure_opportunities',
    },
    {
      type: 'bool',
      name: 'windsurfing',
      label: $gettext('Windsurfing'),
      group: 'leisure_opportunities',
    },
    // {
    //   type: 'bool',
    //   name: 'kitesurfing',
    //   label: $gettext('Kitesurfing'),
    //   group: 'leisure_opportunities',
    // },
    // {
    //   type: 'bool',
    //   name: 'wavesurfing',
    //   label: $gettext('Wave surfing'),
    //   group: 'leisure_opportunities',
    // },
    {
      type: 'bool',
      name: 'sailing',
      label: $gettext('Sailing'),
      group: 'leisure_opportunities',
    },
    {
      type: 'bool',
      name: 'boat_hire',
      label: $gettext('Boat hire'),
      group: 'leisure_opportunities',
    },
    {
      type: 'bool',
      name: 'slipway',
      label: $gettext('Slipway'),
      group: 'leisure_opportunities',
    },
    {
      type: 'bool',
      name: 'bicycle_rent',
      label: $gettext('Bicycle rent'),
      group: 'leisure_opportunities',
    },
    {
      type: 'bool',
      name: 'horseback_riding',
      label: $gettext('Horseback riding'),
      group: 'leisure_opportunities',
    },
    {
      type: 'bool',
      name: 'fishing',
      label: $gettext('Fishing'),
      group: 'leisure_opportunities',
    },
    {
      type: 'bool',
      name: 'diving_station',
      label: $gettext('Diving station'),
      group: 'leisure_opportunities',
    },
    {
      type: 'bool',
      name: 'skilift',
      label: $gettext('Ski lift'),
      group: 'leisure_opportunities',
    },
    {
      type: 'bool',
      name: 'cross_country_skiing',
      label: $gettext('Cross country skiing'),
      group: 'leisure_opportunities',
    },
    {
      type: 'bool',
      name: 'tents',
      label: $gettext('Rental tents'),
      group: 'rental_accommodations',
    },
    {
      type: 'bool',
      name: 'rent_cabins',
      label: $gettext('Rent cabins'),
      group: 'rental_accommodations',
    },
    {
      type: 'bool',
      name: 'caravan',
      label: $gettext('Rental caravans'),
      group: 'rental_accommodations',
    },
    {
      type: 'bool',
      name: 'mobile_bungalows',
      label: $gettext('Mobile bungalows'),
      group: 'rental_accommodations',
    },
    {
      type: 'bool',
      name: 'apartments',
      label: $gettext('Has apartments'),
      group: 'rental_accommodations',
    },
    {
      type: 'bool',
      name: 'dogs_allowed_in_rented_accomodation',
      label: $gettext('Dogs allowed in rented accomodation'),
      group: 'pets',
    },
    {
      type: 'range',
      name: 'category',
      tooltip: $gettext('{value}'),
      range: true,
      names: ['category__gte', 'category__lte'],
      value: [0, 5],
      min: 0,
      max: 5,
      label: $gettext('Category'),
      group: 'equipment',
    },
    {
      type: 'range',
      inverse: false,
      tooltip: $gettext('up to {value}'),
      name: 'count_space_cars__lte',
      value: 0,
      min: 0,
      max: 200,
      step: 20,
      label: $gettext('Max. number of pitches'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'car_free',
      label: $gettext('Car free'),
      group: 'at_the_parking',
    },
    {
      type: 'bool',
      name: 'car_allowed_at_pitch',
      label: $gettext('Car allowed at pitch'),
      group: 'at_the_parking',
    },
    {
      type: 'bool',
      name: 'nudism',
      label: $gettext('Nudism'),
      group: 'vacation_type',
    },
    {
      type: 'bool',
      name: 'dogs_not_allowed',
      label: $gettext('Dogs always forbidden'),
      group: 'pets',
    },
    {
      type: 'bool',
      name: 'dogs_allowed_season',
      label: $gettext('Dogs allowed in main season'),
      group: 'pets',
    },
    {
      type: 'bool',
      name: 'dogs_allowed_off_season',
      label: $gettext('Dogs allowed in off season'),
      group: 'pets',
    },
    {
      type: 'bool',
      name: 'winter_camping',
      label: $gettext('Wintercamping'),
      group: 'vacation_type',
    },
    {
      type: 'bool',
      name: 'half_shadow',
      label: $gettext('Half shadow'),
      group: 'at_the_parking',
    },
    {
      type: 'bool',
      name: 'mini_camping',
      label: $gettext('Minicamping'),
      group: 'vacation_type',
    },
    {
      type: 'bool',
      name: 'durable_camping',
      label: $gettext('Permanent camping'),
      group: 'vacation_type',
    },
    {
      type: 'bool',
      name: 'motorhome_pitches_outside_gate',
      label: $gettext('Motorhome pitches outside the gate'),
      group: 'equipment',
    },
    {
      type: 'bool',
      name: 'caravan_pitches_outside_gate',
      label: $gettext('Caravan pitches outside the gate'),
      group: 'equipment',
    },
    {
      type: 'range',
      name: 'distance_sea',
      inverse: false,
      tooltip: $gettext('up to {value} m'),
      marker: $gettext('{value} m'),
      multiplier: 1000,
      value: 0,
      min: 0,
      max: 2000,
      step: 100,
      label: $gettext('Distance to a sea'),
      group: 'location',
    },
    {
      type: 'range',
      name: 'distance_lake',
      inverse: false,
      tooltip: $gettext('up to {value} m'),
      marker: $gettext('{value} m'),
      multiplier: 1000,
      value: 0,
      min: 0,
      max: 2000,
      step: 100,
      label: $gettext('Distance to a lake'),
      group: 'location',
    },
    {
      type: 'range',
      inverse: false,
      tooltip: $gettext('up to {value} km'),
      marker: $gettext('{value} km'),
      name: 'distance_highway_exit',
      value: 0,
      min: 0,
      max: 20,
      label: $gettext('Distance to a highway exit'),
      group: 'location',
    },
    {
      type: 'range',
      name: 'elevation__gte',
      tooltip: $gettext('over {value} m'),
      marker: $gettext('{value} m'),
      value: 0,
      min: 0,
      max: 2000,
      step: 100,
      label: $gettext('Above Sea Level'),
      group: 'location',
    },
    {
      type: 'bool',
      name: 'is_in_the_mountains',
      label: $gettext('Is in the mountains'),
      group: 'location',
    },
    {
      type: 'bool',
      name: 'near_to_river',
      label: $gettext('Near a river'),
      group: 'location',
    },
    {
      type: 'bool',
      name: 'near_to_town',
      label: $gettext('Near to a town'),
      group: 'location',
    },
    {
      type: 'bool',
      name: 'near_to_station',
      label: $gettext('Near to a train or bus station'),
      group: 'location',
    },

    {
      type: 'rating',
      name: 'rating_avg_kid_friendly6__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average kid friendly (under 6)'),
      group: 'suitable_for',
    },
    {
      type: 'rating',
      name: 'rating_avg_kid_friendly__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average kid friendly'),
      group: 'suitable_for',
    },
    {
      type: 'rating',
      name: 'rating_avg_kid_friendly18__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average kid friendly (under 18)'),
      group: 'suitable_for',
    },
    {
      type: 'rating',
      name: 'rating_avg_guests_with_dogs_friendly__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average guest with dogs friendly'),
      group: 'suitable_for',
    },
    {
      type: 'rating',
      name: 'rating_avg_couple_friendly__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average couple friendly'),
      group: 'suitable_for',
    },
    {
      type: 'rating',
      name: 'rating_avg_senior_friendly__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average senior friendly'),
      group: 'suitable_for',
    },
    {
      type: 'rating',
      name: 'rating_avg_camper_with_tent_friendly__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average camper with tent'),
      group: 'suitable_for',
    },
    {
      type: 'rating',
      name: 'rating_avg_youth_friendly__gte',
      min: 1,
      max: 4,
      displayValue: '{value}+',
      label: $gettext('Average youth friendly'),
      group: 'suitable_for',
    },
    {
      type: 'string',
      name: 'promo',
      label: '',
      group: '',
    },
  ] as const;

  const filterGroups = [
    { name: 'fanclub', label: $gettext('FANCLUB') },
    { name: 'rating', label: $gettext('Rating') },
    { name: 'suitable_for', label: $gettext('Suitable for...') },
    { name: 'perfect_for', label: $gettext('Perfect for...') },
    { name: 'equipment', label: $gettext('Equipment') },
    { name: 'location', label: $gettext('Location') },
    { name: 'at_the_parking', label: $gettext('At the parking place') },
    { name: 'pets', label: $gettext('Pets') },
    { name: 'family_children', label: $gettext('Family & Children') },
    { name: 'swimming_wellness', label: $gettext('Swimming & Wellness') },
    { name: 'leisure_opportunities', label: $gettext('Leisure opportunities') },
    { name: 'fare_catering', label: $gettext('Fare & Catering') },
    { name: 'accessibility', label: $gettext('Accessibility') },
    { name: 'rental_accommodations', label: $gettext('Rental accommodations') },
    { name: 'vacation_type', label: $gettext('Vacation type') },
  ] as const;

  const activeFilterCount = computed(() => {
    return Object.keys(searchStore.selectedFilter).length;
  });

  const filterList = computed(() => {
    return filter.reduce((acc, filter) => {
      const param = filter.names || filter.param || filter.name;
      return Array.isArray(param) ? [...acc, ...param] : [...acc, param];
    }, [] as string[]);
  });

  /********************
   * WATCHER           *
   ********************/
  watch(() => searchStore.selectedFilterGroup, filterOnSelectedFilterGroupChange, { immediate: true });
  watch(() => route.query, filterOnRouteQueryChange, { immediate: true });

  /********************
   * FUNCTIONS         *
   ********************/
  function getActiveFilterByGroup(group: null | string = null, excludeFilter = []) {
    return filter
      .filter(
        filter =>
          filter.name in searchStore.selectedFilter
          && (!group || group === filter.group)
          && !excludeFilter.includes(filter.name),
      )
      .map(filter => filter.label);
  }

  function filterOnRouteQueryChange(newValue: LocationQuery) {
    const selectedFilter = getFilterSetFromQuery(newValue);

    const sortedFilter = {};
    Object.keys(selectedFilter).sort((a, b) => {
      if (a === 'promo') {
        return -1;
      } else if (b === 'promo') {
        return 1;
      }
      return 0;
    }).forEach((key) => {
      sortedFilter[key] = selectedFilter[key];
    });

    searchStore.selectedFilter = sortedFilter;
  }

  function filterOnSelectedFilterGroupChange(group: string | null) {
    const groupResult = filterGroups.find(g => g.name === (group || 'fanclub'));
    selectedFilterGroupLabel.value = groupResult ? groupResult.label : '';
  }

  function deleteFilter(group: string | null, excluded: string[]) {
    let selectedFilter = { ...searchStore.selectedFilter };

    if (group) {
      filter.forEach((filter) => {
        // if filter is not part of group
        if (filter.group !== group || excluded.includes(filter.name)) {
          return;
        }

        if (filter.name in searchStore.selectedFilter) {
          delete selectedFilter[filter.name];
        }
      });
    } else {
      selectedFilter = {};
    }

    updateFilterInUrl(selectedFilter);
    closeModal('CiFilterResetModal');
  }

  function updateFilterInUrl(selectedFilter: SelectedFilter) {
    const query = { ...route.query };

    // remove all filters params from query
    filterList.value.forEach(filter => delete query[filter]);

    Object.keys(selectedFilter).forEach((key) => {
      const filter = getFilterByName(key);
      switch (filter?.type) {
        case 'bool':
          if (selectedFilter[filter.name] === true) {
            query[filter.name] = selectedFilter[filter.name];
          } else {
            delete query[filter.name];
          }
          break;

        case 'date':
          if (selectedFilter[filter.name] instanceof Date) {
            const date = selectedFilter[filter.name];
            const utcDate = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
            query[filter.name] = new Date(utcDate).toISOString().slice(0, 10);
          } else {
            delete query[filter.name];
          }
          break;

        case 'range':
          if (filter.range) {
            // Compare arrays
            if (
              selectedFilter[filter.name]
              && JSON.stringify(selectedFilter[filter.name]) !== JSON.stringify(filter.value)
            ) {
              [query[filter.names[0]], query[filter.names[1]]] = selectedFilter[filter.name];
            } else {
              delete query[filter.name];
            }
          } else if (selectedFilter[filter.name] !== filter.value) {
            query[filter.name] = selectedFilter[filter.name];
          } else {
            delete query[filter.name];
          }
          break;

        case 'rating':
          if (selectedFilter[filter.name] <= filter.max && selectedFilter[filter.name] >= filter.min) {
            query[filter.name] = selectedFilter[filter.name];
          } else {
            delete query[filter.name];
          }
          break;

        case 'bitwise':
          if (!query[filter.param]) {
            query[filter.param] = 0;
          }
          if (!selectedFilter[filter.name]) {
            // Set bit

            query[filter.param] &= ~filter.value;
            // this.$delete(this.filterSet, filter.name);
          } else {
            // Clear bit

            query[filter.param] |= filter.value;
          }
          if (query[filter.param] === 0) {
            delete query[filter.param];
          }

          break;

        case 'string':
          if (selectedFilter[filter.name]) {
            query[filter.name] = selectedFilter[filter.name];
          } else {
            delete query[filter.name];
          }
          break;

        default:
          break;
      }
    });

    delete query.offset;
    return navigateTo({ query }, { replace: true });
  }

  function openResetModal(group: string | null = null, excluded: string[] = []) {
    openModal(
      {
        component: CiFilterResetModal,
        attrs: {
          applyChanges: () => deleteFilter(group, excluded),
          onClose: () => {
            closeModal('CiFilterResetModal');
          },
        },
      },
      {
        clickToClose: true,
        contentTransition: 'translate-y-down',
        contentClass: 'w-[95%] max-w-[800px] p-0',
      },
    );
  }

  function getFilterByGroupName(name, excluded, filterSet = filter) {
    const orderedFilter = filterSet.filter(filter => filter.group === name && !excluded.includes(filter.name));
    const sortedByLabel = sortArrayOfObjectsByKeyAlphabetically('label')(orderedFilter);
    const sortedByRange = sortedByLabel.sort(rangeToTop);

    // if filter group contains 'rating_avg_overall__gte' always bring it to the start of the array
    const overallFilter = sortedByRange.find(element => element.name === 'rating_avg_overall__gte');
    if (overallFilter) {
      const overallIdx = sortedByRange.findIndex(element => element.name === 'rating_avg_overall__gte');
      sortedByRange.splice(overallIdx, 1); // remove
      sortedByRange.splice(0, 0, overallFilter); // add
    }

    return sortedByRange;
  }

  function getFilterByName(name, filterSet = filter) {
    return filterSet.find(filter => filter.name === name);
  }

  function getFilterSetFromQuery(query, filterSet = filter) {
    const selectedFilter: Record<string, any> = {};

    filterSet.forEach((filter) => {
      // check if filter is present in url
      let filterNames = filter.names || filter.param || filter.name;
      filterNames = Array.isArray(filterNames) ? filterNames : [filterNames];

      if (!filterNames.some(name => name in query)) {
        return;
      }

      // filter is in url
      switch (filter.type) {
        case 'bool':
          selectedFilter[filter.name] = JSON.parse(query[filter.name]);
          break;

        case 'date':
          selectedFilter[filter.name] = new Date(query[filter.name]);
          break;

        case 'range':
          if (filter.range) {
            selectedFilter[filter.name] = [parseFloat(query[filter.names[0]]), parseFloat(query[filter.names[1]])];
          } else {
            selectedFilter[filter.name] = parseFloat(query[filter.name]);
          }
          break;

        case 'rating':
          selectedFilter[filter.name] = parseFloat(query[filter.name]);
          break;

        case 'bitwise':
          if ((query[filter.param] & filter.value) === filter.value) {
            selectedFilter[filter.name] = (query[filter.param] & filter.value) === filter.value;
          }
          break;

        case 'string':
          selectedFilter[filter.name] = query[filter.name];
          break;

        default:
          break;
      }
    });

    return selectedFilter;
  }

  function setFilter({ filter, value }: { filter: string; value: any }) {
    // case where filter to set has value 0 but isn't even in the selected filters
    if (value === 0 && !searchStore.selectedFilter[filter]) {
      return;
    }

    const selectedFilter = toRaw({ ...searchStore.selectedFilter, [filter]: value });
    searchStore.serpScrollTop = false;
    updateFilterInUrl(toRaw(selectedFilter));
  }

  return {
    activeFilterCount,
    filterGroups,
    getActiveFilterByGroup,
    getFilterByGroupName,
    getFilterByName,
    getFilterSetFromQuery,
    openResetModal,
    selectedFilterGroupLabel,
    setFilter,
  };
}
